import "./style/index.scss";
import dayjs from "dayjs";
import { Button, Col, Form, Row, Select, Image, Input, DatePicker, Divider, Table, Space, Modal, Flex } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { useEffect, useState } from "react";
import { listarDetalheContaFinanceira, listarSituacaoContaFinanceira } from "../../../../services/contaFinanceiraService";
import { currencyBRL, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { buscarTipoDocumento } from "../../../../services/tipoDocumentoService";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { LeftOutlined, PaperClipOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { NumericFormat } from "react-number-format";
import UploadArquivosComponent from "../../../../components/uploadArquivosComponent";
import { setPageAuditoriaContaFinanceira } from "../../../../redux/slices/auditoriaContaFinanceira/auditoriaContaFinanceiraSlice";
import { listarArquivoAnexo, listarTipoArquivoAnexo } from "../../../../services/arquivoAnexoService";
import { AnexoItemModel } from "../../../../models/arquivoAnexo/anexoItemModel";
import { resetArquivoAnexo, setDataSourceAnexo, setEditingKeyAnexo, setModalArquivo } from "../../../../redux/slices/arquivoAnexo/arquivoAnexoSlice";
import { buscarContaBanco } from "../../../../services/contaBancoService";
import { Vm_ContaBancoModel } from "../../../../models/contaBanco/vm_contaBancoModel";
import { buscarPessoa } from "../../../../services/pessoaService";
import { PessoaModel } from "../../../../models/pessoa/pessoaModel";
import { ArquivoAnexoModel } from "../../../../models/arquivoAnexo/arquivoAnexoModel";
import { buscarPlanoContaFinanceiro } from "../../../../services/planoContaFinanceiroService";
import { buscarCentroCusto } from "../../../../services/centroCustoService";
import { findNodeById } from "../../../../util/functions/findNodeById";
import { EmpresaModel } from "../../../../models/empresa/empresaModel";
import { TipoArquivoAnexoModel } from "../../../../models/arquivoAnexo/tipoArquivoAnexoModel";

dayjs.locale("pt-br");

interface Item {
  key: string;
  idPlanoContaFinanceiro: number | null;
  idCentroCusto: number | null;
  valor: number | null;
}

const VisualizacaoPage = () => {
  //estados globais
  const contaFinanceiraState = useAppSelector((state) => state.contaFinanceira);
  const auditoriaContaFinanceiraState = useAppSelector((state) => state.auditoriaContaFinanceira);
  const empresaState = useAppSelector((state) => state.empresa);
  const pessoaState = useAppSelector((state) => state.pessoa);
  const contaBancoState = useAppSelector((state) => state.contaBanco);
  const tipoDocumentoState = useAppSelector((state) => state.tipoDocumento);
  const planoContaFinanceiroState = useAppSelector((state) => state.planoContaFinanceiro);
  const centroCustoState = useAppSelector((state) => state.centroCusto);
  const arquivoAnexoState = useAppSelector((state) => state.arquivoAnexo);
  const sorteioState = useAppSelector((state) => state.sorteio);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [formManual] = Form.useForm();
  const [totalItens, setTotalItens] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [dataSource, setData] = useState<Item[]>([]);

  const fetchData = async () => {
    const retorno = await dispatch(listarDetalheContaFinanceira(auditoriaContaFinanceiraState.entity.id));
    var initialData: Item[] = [];
    for (let i = 0; i < retorno.payload.length; i++) {
      initialData.push({
        key: i.toString(),
        idPlanoContaFinanceiro: retorno.payload[i].idPlanoContaFinanceiro,
        idCentroCusto: retorno.payload[i].idCentroCusto,
        valor: retorno.payload[i].valor,
      });
    }
    let soma = retorno.payload.reduce((prev: any, current: any) => {
      return prev + +(current.valor === null ? 0 : realParaNumber(current.valor.toString()));
    }, 0);
    setTotalItens(soma);
    setData(initialData);
  };

  useEffect(() => {
    dispatch(setTitle("VISUALIZAÇÃO DE AUDITORIA DE CONTAS FINANCEIRAS"));
    dispatch(listarSituacaoContaFinanceira());
    dispatch(buscarTipoDocumento(tipoDocumentoState.busca));
    dispatch(buscarPlanoContaFinanceiro(planoContaFinanceiroState.busca));
    dispatch(buscarPessoa(pessoaState.busca));
    dispatch(buscarContaBanco(contaBancoState.busca));
    dispatch(buscarCentroCusto(centroCustoState.busca));
    dispatch(listarTipoArquivoAnexo("cfin"));
    fetchData();
    form.setFieldsValue({ ...auditoriaContaFinanceiraState.entity });
    form.setFieldValue("dataEmissao", dayjs(new Date(auditoriaContaFinanceiraState.entity.dataEmissao)));
    form.setFieldValue("dataVencimento", dayjs(new Date(auditoriaContaFinanceiraState.entity.dataVencimento)));
    form.setFieldValue("dataCompetencia", dayjs(new Date(auditoriaContaFinanceiraState.entity.dataCompetencia)));
    setTotal(auditoriaContaFinanceiraState.entity.valorBruto);
  }, []);

  const columns = [
    {
      title: "PLANO DE CONTA FINANCEIRO",
      dataIndex: "idPlanoContaFinanceiro",
      editable: true,
      render: (_: any, record: any) => findNodeById(planoContaFinanceiroState.list, record.idPlanoContaFinanceiro)?.denominacao,
    },
    {
      title: "CENTRO DE CUSTO",
      dataIndex: "idCentroCusto",
      editable: true,
      render: (_: any, record: any) => findNodeById(centroCustoState.list, record.idCentroCusto)?.denominacao,
    },
    {
      title: "VALOR BRUTO",
      dataIndex: "valor",
      editable: true,
      align: "right" as "right",
      render: (_: any, { valor }: any) => currencyBRL(valor === null ? 0 : valor),
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === "valor" ? "number" : col.dataIndex === "idCentroCusto" ? "selectCentro" : "selectConta",
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });

  const voltar = () => {
    dispatch(setTitle("AUDITORIA DE CONTAS FINANCEIRAS"));
    dispatch(setPageAuditoriaContaFinanceira("geral"));
  };

  const goAnexarArquivo = async () => {
    const retorno = await dispatch(listarArquivoAnexo(auditoriaContaFinanceiraState.entity.id));
    let imagens = retorno.payload.filter((a: ArquivoAnexoModel) => arquivoAnexoState.listTipo.some((tipo: TipoArquivoAnexoModel) => tipo.id === a.idTipoArquivoAnexo));

    if (imagens.length !== 0) {
      var initialData: AnexoItemModel[] = [];
      for (let i = 0; i < imagens.length; i++) {
        initialData.push({
          key: i.toString(),
          arquivo: {
            uid: "-1",
            name: imagens[i].nome,
            status: "done",
            type: imagens[i].extensaoArquivo,
            url: imagens[i].caminhoArquivo,
          },
          caminhoArquivo: imagens[i].caminhoArquivo,
          idTipoArquivoAnexo: imagens[i].idTipoArquivoAnexo,
          observacao: imagens[i].observacao,
        });
      }
      dispatch(setDataSourceAnexo(initialData));
      dispatch(setEditingKeyAnexo(""));
    }
    dispatch(setModalArquivo(true));
  };

  const closeModalArquivo = () => {
    dispatch(resetArquivoAnexo());
    dispatch(setModalArquivo(false));
  };

  return (
    <Flex gap={12} vertical>
      <Flex justify="space-between" align="center">
        <Flex onClick={voltar} className="voltar">
          {"<"} VOLTAR
        </Flex>
        <Button className="buttonPrimary" onClick={goAnexarArquivo} icon={<PaperClipOutlined />}>
          ANEXOS
        </Button>
      </Flex>
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={7}>
            <Form.Item label="Empresa" name="idEmpresa">
              <Select
                showSearch
                optionFilterProp="labelSearch"
                loading={empresaState.loading}
                style={{ width: "100%" }}
                options={empresaState.list.map((a: EmpresaModel) => ({
                  value: a.id,
                  key: a.apelido,
                  label: (
                    <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                      <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                      {a.apelido}
                    </Flex>
                  ),
                  labelSearch: a.apelido,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Conta banco" name="idContaBanco">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Selecione a conta banco"
                loading={contaBancoState.loading}
                options={contaBancoState.list.map((a: Vm_ContaBancoModel) => ({
                  value: a.id,
                  label: a.denominacaoPersonalizada,
                  disabled: !a.ativo,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item label="Sorteio" name="idSorteio">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Selecione o sorteio"
                loading={sorteioState.loading}
                options={sorteioState.list.map((a: any) => ({
                  value: a.idSorteio,
                  label: a.observacao === null ? dayjs(a.dataSorteio).format("DD/MM/YYYY") : dayjs(a.dataSorteio).format("DD/MM/YYYY") + " - " + a.observacao,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Tipo de documento" name="idTipoDocumento">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Selecione o tipo de documento"
                loading={tipoDocumentoState.loading}
                options={tipoDocumentoState.list.map((a: any) => ({
                  value: a.id,
                  label: a.denominacao,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Número do documento" name="numeroDocumento">
              <Input type="text" disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={"Fornecedor/Cliente"} name="idPessoa">
              <Select
                showSearch
                optionFilterProp="label"
                loading={pessoaState.loading}
                options={pessoaState.list.map((a: PessoaModel) => ({
                  value: a.id,
                  label: a.apelido,
                  disabled: !a.ativo,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Favorecido" name="idPessoaFavorecido">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Selecione o favorecido"
                loading={pessoaState.loading}
                options={pessoaState.list.map((a: PessoaModel) => ({
                  value: a.id,
                  label: a.apelido,
                  disabled: !a.ativo,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12} style={{ width: 1100 }}>
          <Col span={4}>
            <Form.Item label="Data de emissão" name="dataEmissao">
              <DatePicker format={"DD/MM/YYYY"} disabled />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Data de vencimento" name="dataVencimento">
              <DatePicker format={"DD/MM/YYYY"} disabled />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Data de competência" name="dataCompetencia">
              <DatePicker picker="month" format={"MM/YYYY"} disabled />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Status" name="idSituacaoContaFinanceira">
              <Select
                showSearch
                optionFilterProp="label"
                loading={contaFinanceiraState.loadingSituacao}
                options={contaFinanceiraState.listSituacao.map((a: any) => ({
                  value: a.id,
                  label: a.denominacao,
                }))}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item label="Histórico" name="historico">
              <TextArea rows={3} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12} style={{ width: 1200 }}>
          <Col span={4}>
            <Form.Item label="Valor bruto" name="valorBruto">
              <NumericFormat className="ant-input noBoderColor number-input" thousandSeparator="." decimalSeparator="," decimalScale={2} prefix="R$" fixedDecimalScale={true} disabled />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Valor líquido" name="valorLiquido">
              <NumericFormat className="ant-input noBoderColor number-input" thousandSeparator="." decimalSeparator="," decimalScale={2} prefix="R$" fixedDecimalScale={true} disabled />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Valor pago" name="valorPago">
              <NumericFormat className="ant-input noBoderColor number-input" thousandSeparator="." decimalSeparator="," decimalScale={2} prefix="R$" fixedDecimalScale={true} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left" orientationMargin="0">
          Classificação das contas financeiras
        </Divider>
        <>
          <Row gutter={12}>
            <Col span={20}></Col>
            <Col span={4} className="totalCol">
              <Form.Item label="Total dos itens">
                <NumericFormat
                  className="ant-input noBoderColor number-input"
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  prefix="R$"
                  fixedDecimalScale={true}
                  disabled
                  value={totalItens}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form form={formManual} component={false}>
            <Row gutter={12}>
              <Col span={24} className="divManual">
                <Form.Item>
                  <Table size="small" rowClassName="editable-row" dataSource={dataSource} columns={mergedColumns} pagination={false} loading={contaFinanceiraState.loading} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </>
        <Row gutter={12}>
          <Col span={24} className="modalFooter">
            <Space size="middle">
              <Button className="cancelButton" onClick={voltar} icon={<LeftOutlined />}>
                VOLTAR
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
      <Modal title={"ANEXOS"} centered width={1100} open={arquivoAnexoState.modalArquivo} onCancel={closeModalArquivo} footer={null} destroyOnHidden>
        <UploadArquivosComponent visualizar={true} idOrigem={auditoriaContaFinanceiraState.entity.id} container={"contafinanceira"} origem="auditoria" />
      </Modal>
    </Flex>
  );
};

export default VisualizacaoPage;
